import { createAsyncThunk } from "@reduxjs/toolkit"

import {
  type EmbedRequestFailure,
  type ResolvedEmbedChatroom,
  resolveEmbedChatroom,
  toEmbedRequestFailure
} from "@/common/services/chat"

import type { EmbedRootState } from "../embedSelectors"

interface ReopenClosedEmbedChatParams {
  /** The chat the backend just refused with `CONVERSATION_CLOSED`. */
  readonly closedChatId: string
}

export interface ReopenedEmbedChat extends ResolvedEmbedChatroom {
  readonly closedChatId: string
}

/**
 * Re-initialise a widget tab whose conversation was deleted while it was open.
 *
 * Retention's DELETE level hides a conversation from its visitor; every chat door
 * then answers `CONVERSATION_CLOSED`. This re-runs the resume for the SAME session
 * — which never hands out a DELETED chat, so it answers a fresh reserved id — and
 * the slice's `fulfilled` case swaps the closed chat for the fresh one: the closed
 * entry and its transcript are dropped and `activeChatId` moves, so every reader
 * keyed on the active chat follows without being told.
 *
 * It resends nothing. `deliverEmbedMessage` owns the one resend a refused SEND
 * earns; a refused READ (history, download, export) only re-initialises.
 *
 * `condition` skips the reopen once the closed chat has already been replaced, so
 * a second refusal of the same chat does not resume twice.
 */
export const reopenClosedEmbedChat = createAsyncThunk<
  ReopenedEmbedChat,
  ReopenClosedEmbedChatParams,
  { state: EmbedRootState; rejectValue: EmbedRequestFailure }
>(
  "embedChat/reopenClosedChat",
  async ({ closedChatId }, { getState, rejectWithValue }) => {
    const closed = getState().embedChat.chats[closedChatId]
    const agentId = closed?.deployment?.agent?.id

    if (!closed || !agentId) {
      return rejectWithValue({ message: "No deployment to reopen the conversation in", code: null })
    }

    try {
      const resolved = await resolveEmbedChatroom({
        deploymentId: closed.integrationId,
        sessionId: closed.sessionId,
        agentId
      })
      return { closedChatId, ...resolved }
    } catch (error) {
      return rejectWithValue(toEmbedRequestFailure(error, "Failed to reopen the conversation"))
    }
  },
  {
    condition: ({ closedChatId }, { getState }) =>
      getState().embedChat.chats[closedChatId] !== undefined
  }
)
